import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PhotoPicker } from "./shared/PhotoPicker";
import { PhotoThumb } from "./shared/PhotoThumb";
import { MachineSelect } from "./shared/MachineSelect";
import { FilterBar } from "./shared/FilterBar";
import { useMyAssets } from "./shared/useMyAssets";
import { NoAccess } from "./shared/NoAccess";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationBar } from "@/components/shared/PaginationBar";

const VIEW_ROLES = ["Director", "Manager", "Accounts", "Back Office"];

function SubmitForm() {
  const { assets, needsPicker } = useMyAssets();
  const [machineAssetId, setMachineAssetId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (needsPicker && !machineAssetId) return toast.error("Please select which machine this entry is for.");
    setSubmitting(true);
    const fd = new FormData();
    if (remarks) fd.append("remarks", remarks);
    if (machineAssetId) fd.append("machine_asset_id", machineAssetId);
    photos.forEach((f) => fd.append("photos", f));
    try {
      await client.post("/fleetcare/site-log-upload", fd);
      toast.success("Site photo uploaded");
      setRemarks("");
      setPhotos([]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="site-log-upload-submit-form">
      {needsPicker && <MachineSelect assets={assets} value={machineAssetId} onChange={setMachineAssetId} testId="site-log-upload-machine-select" />}
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
        <Textarea data-testid="site-log-upload-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>
      <PhotoPicker label="Site Photo" required files={photos} onChange={setPhotos} testId="site-log-upload-photo-picker" />
      <Button type="submit" data-testid="site-log-upload-submit-button" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
        {submitting ? "Uploading..." : "Upload Site Photo"}
      </Button>
    </form>
  );
}

function DetailPanel({ entry, onClose }) {
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="site-log-upload-detail-panel">
        <SheetHeader>
          <SheetTitle>{entry.asset_label}</SheetTitle>
          <SheetDescription>
            {entry.employee_name} · {new Date(entry.created_at).toLocaleString("en-IN")}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2 flex-wrap">
            {(entry.photos || []).map((p, i) => (
              <PhotoThumb key={i} path={`/fleetcare/site-log-upload/${entry.id}/photos/${i}`} alt={p.filename} testId={`site-log-upload-photo-${i}`} />
            ))}
          </div>
          {entry.remarks && (
            <p className="text-sm">
              <span className="text-slate-500">Remarks:</span> {entry.remarks}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ViewList() {
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", assetId: "" });
  const params = {};
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.assetId) params.asset_id = filters.assetId;
  const { items: entries, loading, page, setPage, totalPages } = usePaginatedList("/fleetcare/site-log-upload", params);
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-4" data-testid="site-log-upload-view">
      <FilterBar {...filters} onChange={(v) => setFilters((f) => ({ ...f, ...v }))} testIdPrefix="site-log-upload" />
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="site-log-upload-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  No entries found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`site-log-upload-row-${e.id}`} className="cursor-pointer" onClick={() => setSelected(e)}>
                  <TableCell>{new Date(e.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="font-mono text-xs">{e.asset_label}</TableCell>
                  <TableCell>{e.employee_name}</TableCell>
                  <TableCell className="truncate max-w-xs">{e.remarks || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} testId="site-log-upload-pagination" />
      {selected && <DetailPanel entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default function SiteLogUploadTab() {
  const { user } = useAuth();
  if (user?.role === "Operator") return <SubmitForm />;
  if (VIEW_ROLES.includes(user?.role)) return <ViewList />;
  return <NoAccess />;
}
